import axios, { type AxiosInstance } from 'axios';
import authService from './auth.service';
import { API_URL } from '../lib/config';

/** The banner fields shared by the authenticated `/active` feed and the public
 *  `/status` feed — all that the banner + pre-maintenance modal actually read. */
export interface MaintenanceWindowView {
  starts_at: string;
  ends_at: string;
  reason_short: string;
  status: 'scheduled' | 'cancelled' | 'completed';
  is_active: boolean;
}

export interface MaintenanceWindow extends MaintenanceWindowView {
  id: string;
  email_send_at: string;
  banner_show_from: string;
  reason_long: string | null;
  email_sent_at: string | null;
  created_by: string;
  cancelled_by: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface ActiveWindowResponse {
  window: MaintenanceWindow | null;
}

/** Public (unauthenticated) banner feed — minimal fields only (#54). */
export interface PublicStatusResponse {
  window: MaintenanceWindowView | null;
}

export interface ScheduleWindowPayload {
  starts_at: string;
  ends_at: string;
  email_send_at: string;
  banner_show_from: string;
  reason_short: string;
  reason_long?: string | null;
}

class MaintenanceService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async getActive(): Promise<ActiveWindowResponse> {
    const res = await this.api.get<ActiveWindowResponse>('/api/v1/maintenance/active');
    return res.data;
  }

  /** Unauthenticated banner feed for the login page (#54). Bare axios GET (no
   *  auth interceptors) — there's no session yet on a public page. */
  async getPublicStatus(): Promise<PublicStatusResponse> {
    const res = await axios.get<PublicStatusResponse>(`${API_URL}/api/v1/maintenance/status`);
    return res.data;
  }

  async listWindows(): Promise<MaintenanceWindow[]> {
    const res = await this.api.get<{ items: MaintenanceWindow[] }>(
      '/api/v1/admin/maintenance/windows',
    );
    return res.data.items;
  }

  async schedule(payload: ScheduleWindowPayload): Promise<MaintenanceWindow> {
    const res = await this.api.post<MaintenanceWindow>(
      '/api/v1/admin/maintenance/windows',
      payload,
    );
    return res.data;
  }

  async cancel(windowId: string, reason?: string | null): Promise<MaintenanceWindow> {
    const res = await this.api.patch<MaintenanceWindow>(
      `/api/v1/admin/maintenance/windows/${windowId}/cancel`,
      { reason: reason ?? null },
    );
    return res.data;
  }
}

export default new MaintenanceService();
